import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UserContext } from '../../../context/UserContext';
import Places, { PlaceToSend } from '../../../service/Places';
import { Label } from '../../../StyledComponents/Input/generic';
import PlacesAutocomplete from '../PlacesAutoConplete';
import AttToken from '../../../helpers/attToken';
import queryClient from '../../../service/query';
import { trackEventMatomo } from '../../../helpers/matomo';
import { TextField } from '@mui/material';
import HeaderModal from '../../HeaderModal/HeaderModal';
import FooterModal from '../../FooterModal/FooterModal';
import QueryFn from '../../../helpers/QueryFn';

const PlaceService = new Places();
const queryFn = new QueryFn();

export type selectedPlaceType = {
  lat: number, lng: number, address: string
};

export default function NewPlaceModal({ getPlaces }: { getPlaces: Function }) {
  const { customerId, escape, placesList, setPlacesList, editPlace, isEditing, setIsEditing } = useContext(UserContext);
  const { t } = useTranslation('translation');
  const { popUp, openModal, hierarchyLabel } = useContext(UserContext);
  const [selected, setSelected] = useState<selectedPlaceType>({ lat: 0, lng: 0, address: '' });
  const [name, setName] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    if (isEditing && editPlace) {
      setName(editPlace.nome);
      setSelected({
        lat: editPlace.lat,
        lng: editPlace.lng,
        address: editPlace.localizacao,
      });
    }
  }, [editPlace, isEditing]);

  const { mutate: create } = queryFn.useMutate('CREATE_PLACE',
    async () => {
      setIsDisabled(true);
      try {
        const token = await AttToken();
        const PlaceObj: PlaceToSend = {
          userOwner: 'Administrador Axyma',
          cliente: customerId,
          conjuntos: [],
          dataAlteracao: Date.now(),
          lat: selected.lat,
          lng: selected.lng,
          localizacao: selected.address,
          nome: name,
        };
        if (token) {
          const newPlace = await PlaceService.newPlace(token, PlaceObj);
          openModal();
          placesList && setPlacesList([...placesList, newPlace.local]);
          getPlaces(customerId);
          toast.success(`${hierarchyLabel.local} ${t('created')}!`);
          queryClient.resetQueries({ queryKey: ['GET_LOCAIS'] });
        }
      } catch (err) {
        toast.error(`${t('Services.ServiceModal.errorCreatingNewLocation')}`);
        return console.log(err);
      }
    });

  const { mutate: edit } = queryFn.useMutate('EDITE_PLACE',
    async () => {
      setIsDisabled(true);
      try {
        const token = await AttToken();
        if (token && editPlace) {
          const response = await PlaceService.editPlace(token, {
            lat: selected.lat,
            lng: selected.lng,
            localizacao: selected.address,
            nome: name,
            id: editPlace.id
          });
          placesList && setPlacesList(placesList.map((e) => e.id === response.id ? response : e));
          openModal();
          getPlaces(customerId);
          setIsEditing(false);
          toast.success(`${hierarchyLabel.local} ${t('edited')}!`);
          queryClient.resetQueries({ queryKey: ['GET_LOCAIS'] });
        }
      } catch (err) {
        return console.log(err);
      }
    });

  const close = () => {
    trackEventMatomo('Modal criação de local', 'click', 'div', 'fecha modal');
    openModal();
    setName('');
    setSelected({ lat: 0, lng: 0, address: '' });
    setIsEditing(false);
  };

  const submit = () => {
    trackEventMatomo('Modal criação de local', 'click', 'button', 'salvar');
    isEditing ? edit('') : create('');
  };

  return (
    <div className={`modal ${popUp ? "modal is-active" : "modal"}`}>
      <div
        className="modal-background"
        onKeyDown={() => { escape(); }} onClick={close} />
      <div className="modal-card" onSubmit={(e: React.FormEvent) => e.preventDefault()}>
        <HeaderModal
          close={close}
          headerTitle={isEditing
            ? `${t('Services.ServiceModal.editLocation')}`
            : `${t('Services.ServiceModal.addLocation')}`}
        />
        <div className="modal-card-body">
          <TextField
            onClick={() => trackEventMatomo('Modal criação de local', 'click', 'textfield', 'edita valor')}
            style={{ marginBottom: '10px' }}
            label={t('name') + ':'}
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Label>
            {t('address') + ':'}
          </Label>
          <p style={{ color: 'light-gray', fontSize: '.6em', fontWeight: 700 }}>
          </p>
          <PlacesAutocomplete selected={selected} setSelected={setSelected} />
          {(!selected.lng || !selected.lng) && (
            <span className='tag is-warning'>{t('Services.ServiceModal.pleaseSelectLocationFromList')}</span>
          )}
        </div>
        <FooterModal
          label={`${t('save')}`}
          submit={submit}
          disabled={name === ''
            || selected.address === ''
            || !selected.lat
            || !selected.lng
            || isDisabled} />
      </div>
    </div>
  );
}
